import { Injectable, computed, signal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, lastValueFrom } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import { StorageService } from './storage.service';
import { UserRoles } from '../constants/constants';
import {
  ClientPortalProject, ClientPortalProjectList, ClientPortalProjectListOptions, ClientPortalSession
} from '../interfaces/client-portal.interface';

const SESSION_STORAGE_KEY = 'clientPortalSession';

/**
 * Client-portal API (MP-5750 over the MP-5748 backend namespace).
 *
 * The backend derives everything from the session user: no firm, group or
 * tenant id travels in any request, and the token interceptor sends an empty
 * X-Tenant-Id for this role, which the backend accepts. `/client-portal` is
 * the ONLY resource a CLIENT token may reach; `configurations`, `my`,
 * `documents`, `firms`, `projects`, `users` all answer 403, and the auth
 * interceptor turns a 403 into a forced logout. So the dashboard shell must
 * never fire those for a CLIENT, and this service is the only data source.
 *
 * The session block is fetched once at login and kept in localStorage so the
 * header and sidenav can read it synchronously after a page reload. Logout
 * clears localStorage, which clears it too.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientPortalService {

  /** Session block for the signed-in CLIENT, or null for every other role. */
  readonly session = signal<ClientPortalSession | null>(null);
  readonly firmName = computed(() => this.session()?.firm?.name || '');
  readonly clientGroupName = computed(() => this.session()?.clientGroup?.name || '');
  readonly userName = computed(() => {
    const user = this.session()?.user;
    return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  });

  constructor(private http: HttpBaseService, private storageService: StorageService) {
    this.session.set(this.storageService.getLocalStorageItem(SESSION_STORAGE_KEY) || null);
  }

  /** True when the role cookie says the signed-in user is a client-portal user. */
  isClientPortalUser(): boolean {
    return this.storageService.getRole() === UserRoles.client;
  }

  getSession(): Observable<ClientPortalSession> {
    return this.http.get('client-portal/session');
  }

  /** Fetches the session block and caches it for the shell. Call once right after a CLIENT login. */
  async loadSession(): Promise<ClientPortalSession> {
    const session = await lastValueFrom(this.getSession());
    this.session.set(session);
    this.storageService.setLocalStorageItem(SESSION_STORAGE_KEY, session);
    return session;
  }

  clearSession(): void {
    this.session.set(null);
    this.storageService.setLocalStorageItem(SESSION_STORAGE_KEY, null);
  }

  /** Projects shared with the user's group; STARTED, PUBLISHED and ARCHIVED only, `{projects, count}` like the firm list. */
  getProjects(options: ClientPortalProjectListOptions = {}): Observable<ClientPortalProjectList> {
    let params = new HttpParams();
    (Object.keys(options) as Array<keyof ClientPortalProjectListOptions>).forEach((key) => {
      const value = options[key];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get('client-portal/projects', params.keys().length ? { params } : undefined);
  }

  /** One shared project; the backend answers 404 (never 403) for anything outside the user's group. */
  getProject(projectId: string): Observable<ClientPortalProject> {
    return this.http.get(`client-portal/projects/${projectId}`);
  }
}
